import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { LuUsers } from "react-icons/lu";
import { FiSearch } from "react-icons/fi";

import ListPageShell from "../list-page/ListPageShell";
import ListPageHeader from "../list-page/ListPageHeader";
import ListPageSearch from "../list-page/ListPageSearch";
import ListPageEmpty from "../list-page/ListPageEmpty";
import ListPager from "../list-page/ListPager";

import PrizesTable from "./PrizesTable";
import WinnerDetails from "./WinnerDetails";
import GameDetails from "../game_rules/GameDetails";
import ReceiptView from "../ReceiptView";

import { useGetWinnersQuery } from "../../store/services/prizeServices";
import { winnerDetailsActions } from "../../store/winnerDetailsSlice";
import { gameDetailsActions } from "../../store/gameDetailsSlice";
import { receiptViewActions } from "../../store/receiptViewSlice";

const PAGE_LIMIT = 10;

export default function PrizesMain() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isWinnerDetailsOpen = useSelector((s) => s.winner_details.isOpen);
  const { isOpen: isGameDetailsOpen } = useSelector((s) => s.game_details);
  const isReceiptViewOpen = useSelector((s) => s.receipt_view.isOpen);

  const [filteredPrizes, setFilteredPrizes] = useState([]);
  const [pages, setPages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState({});
  const [searchTextInput, setSearchTextInput] = useState("");
  const [query] = useState(new URLSearchParams(window.location.search));

  const { data, isLoading, isFetching } = useGetWinnersQuery(params, {
    refetchOnMountOrArgChange: true,
  });

  /* Hydrate from URL */
  useEffect(() => {
    const newParams = {};
    query.forEach((v, k) => (newParams[k] = v));
    setParams(newParams);
    if (query.has("page")) setCurrentPage(parseInt(query.get("page")));
    if (query.has("q")) setSearchTextInput(query.get("q"));
  }, [query]);

  /* Sync data */
  useEffect(() => {
    if (data) {
      setFilteredPrizes(data?.data?.data || []);
      setPages({
        currentPage: data?.data?.currentPage,
        totalPages: data?.data?.totalPages,
        totalCount: data?.data?.totalCount,
      });
    }
  }, [data]);

  /* Reflect params into URL */
  useEffect(() => {
    const urlParams = new URLSearchParams(params).toString();
    navigate(`/winners/individuals?${urlParams}`, { replace: true });
  }, [params, navigate]);

  /* Search debounce */
  const debounceRef = useRef(null);
  const handleSearchTextInputChange = (e) => {
    const text = e.target.value;
    setSearchTextInput(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length > 2) {
      debounceRef.current = setTimeout(() => {
        setParams((p) => ({ ...p, q: text, page: 1 }));
        setCurrentPage(1);
      }, 400);
    } else {
      setParams((p) => {
        const { q, ...rest } = p;
        return rest;
      });
    }
  };

  const handleSort = (value) => {
    setParams((p) => {
      if (p.sort_by === value && p.sort_order === "asc") {
        const { sort_by, sort_order, ...rest } = p;
        return rest;
      }
      return {
        ...p,
        sort_by: value,
        sort_order: p.sort_by === value && p.sort_order === "desc" ? "asc" : "desc",
      };
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setPages((p) => ({ ...p, currentPage: page }));
    setParams((p) => ({ ...p, page }));
  };

  const handleRowClick = (prize) => {
    dispatch(winnerDetailsActions.openModal(prize));
  };

  const handleGameClick = (prize) => {
    dispatch(gameDetailsActions.openModal({ game_id: prize.game_id }));
  };

  /* ReceiptView loads the full receipt itself from receipt_id */
  const handleReceiptClick = (prize) => {
    dispatch(receiptViewActions.openModal({ receipt_id: prize.receipt_id }));
  };

  const hasFilters = !!params.q;

  const handleClearFilters = () => {
    setSearchTextInput("");
    setCurrentPage(1);
    setParams({});
  };

  const isEmpty = !isLoading && !isFetching && filteredPrizes.length === 0;

  const totalCount = pages?.totalCount ?? 0;
  const rangeLabel = useMemo(() => {
    if (!totalCount) return "";
    const start = (currentPage - 1) * PAGE_LIMIT + 1;
    const end = Math.min(currentPage * PAGE_LIMIT, totalCount);
    return `Po shfaqen ${start}–${end} prej ${totalCount.toLocaleString()}`;
  }, [currentPage, totalCount]);

  return (
    <>
      {isWinnerDetailsOpen && <WinnerDetails />}
      {isGameDetailsOpen && <GameDetails />}
      {isReceiptViewOpen && <ReceiptView />}

      <ListPageShell>
        <ListPageHeader
          eyebrow={{ icon: <LuUsers size={18} />, label: "Fituesit (individë)" }}
          title="Qytetarët fitues"
          subtitle={
            totalCount
              ? `${totalCount.toLocaleString()} fitues individualë`
              : "Lista e fituesve individualë"
          }
        />

        <ListPageSearch
          value={searchTextInput}
          onChange={handleSearchTextInputChange}
          placeholder="Kërko sipas emrit, email-it ose lojës..."
          hasFilters={hasFilters}
          onClearFilters={handleClearFilters}
        />

        {isEmpty ? (
          <ListPageEmpty
            icon={hasFilters ? FiSearch : LuUsers}
            title={hasFilters ? "Asnjë fitues nuk u gjet" : "Ende asnjë fitues"}
            subtitle={
              hasFilters
                ? "Provoni të ndryshoni ose të pastroni filtrat për të parë më shumë rezultate."
                : "Fituesit do të shfaqen këtu pasi të gjenerohen për një lojë."
            }
            action={
              hasFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                >
                  Pastro filtrat
                </button>
              )
            }
          />
        ) : (
          <PrizesTable
            prizes={filteredPrizes}
            isLoading={isLoading || isFetching}
            params={params}
            onSort={handleSort}
            onRowClick={handleRowClick}
            onGameClick={handleGameClick}
            onReceiptClick={handleReceiptClick}
          />
        )}

        {!isEmpty && (
          <ListPager
            currentPage={currentPage}
            totalPages={pages?.totalPages || 1}
            onChange={handlePageChange}
            rangeLabel={rangeLabel}
          />
        )}
      </ListPageShell>
    </>
  );
}
